import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Container, Box, Typography, Chip, Button, CircularProgress, Tooltip,
  Snackbar, Alert, TextField, InputAdornment, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import { getStripeRestaurantAccounts, getStripeRestaurantDashboardGlobal } from '../apollo/queries'
import {
  createStripeRestaurantAccount,
  stripeRestaurantOnboardingLink,
  refreshStripeRestaurantAccount,
  payOutPendingStripeTransfers,
  setStripeRestaurantDashboard,
  removeStripeRestaurantAccount,
  setStripeRestaurantProcessingFee,
} from '../apollo/mutations'

const GET_ACCOUNTS = gql`${getStripeRestaurantAccounts}`
const GET_DASHBOARD_GLOBAL = gql`${getStripeRestaurantDashboardGlobal}`
const CREATE_ACCOUNT = gql`${createStripeRestaurantAccount}`
const ONBOARDING_LINK = gql`${stripeRestaurantOnboardingLink}`
const REFRESH_ACCOUNT = gql`${refreshStripeRestaurantAccount}`
const PAY_OUT_PENDING = gql`${payOutPendingStripeTransfers}`
const SET_DASHBOARD = gql`${setStripeRestaurantDashboard}`
const REMOVE_ACCOUNT = gql`${removeStripeRestaurantAccount}`
const SET_FEE = gql`${setStripeRestaurantProcessingFee}`

// "3.5% + $0.30"
const feeLabel = (percent, fixed) => `${Number(percent || 0)}% + $${Number(fixed || 0).toFixed(2)}`

// Same visual language as the QorCommerce pages.
const INK = '#1c2024'
const MUTED = '#697177'
const LINE = '#e8eaed'
const GOOD = '#12a594'
const WAIT = '#f5a524'

const usd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`)

// The three things Stripe tells us about an account, in the order they happen.
function Stage({ done, label }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: done ? '#e6f5f3' : '#fdf4e3',
        color: done ? '#0b6e63' : '#8a5a00',
        border: `1px solid ${done ? '#bfe7e1' : '#f3e2bd'}`,
      }}
    />
  )
}

export default function StripeRestaurants() {
  const { data, loading, error, refetch } = useQuery(GET_ACCOUNTS, { fetchPolicy: 'cache-and-network' })
  const [createAccount] = useMutation(CREATE_ACCOUNT)
  const [onboardingLink] = useMutation(ONBOARDING_LINK)
  const [refreshAccount] = useMutation(REFRESH_ACCOUNT)
  const [payOutPending] = useMutation(PAY_OUT_PENDING)
  const [setDashboard] = useMutation(SET_DASHBOARD)
  const [removeAccount] = useMutation(REMOVE_ACCOUNT)
  // The row whose connected account is about to be deleted, while confirming.
  const [removing, setRemoving] = useState(null)
  const [setFee] = useMutation(SET_FEE)
  // { row, percent, fixed } while a restaurant's processing fee is being edited.
  // Blank fields mean "use the default".
  const [feeEdit, setFeeEdit] = useState(null)
  const { data: globalData } = useQuery(GET_DASHBOARD_GLOBAL, { fetchPolicy: 'cache-and-network' })
  const globalOn = !!globalData?.configuration?.stripeRestaurantDashboard

  const [busy, setBusy] = useState(null)
  const [toast, setToast] = useState(null)
  const [search, setSearch] = useState('')

  const rows = (data?.stripeRestaurantAccounts || []).filter((r) =>
    !search || (r.restaurantName || '').toLowerCase().includes(search.toLowerCase()),
  )

  // Every action is one network call that can fail on Stripe's side, so each
  // one reports back rather than silently doing nothing.
  const run = async (key, fn) => {
    setBusy(key)
    try {
      await fn()
    } catch (e) {
      setToast({ severity: 'error', text: e.message })
    } finally {
      setBusy(null)
    }
  }

  const copyLink = (row) =>
    run(`link-${row.restaurantId}`, async () => {
      const res = await onboardingLink({ variables: { restaurantId: row.restaurantId } })
      const url = res?.data?.stripeRestaurantOnboardingLink?.url
      if (!url) throw new Error('Stripe did not return an onboarding link')
      try {
        await navigator.clipboard.writeText(url)
        setToast({ severity: 'success', text: 'Onboarding link copied. Send it to the owner — it expires.' })
      } catch {
        // Clipboard is blocked in some browsers; show the link so it can still be copied by hand.
        setToast({ severity: 'info', text: url })
      }
    })

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: INK }}>
        Stripe Restaurants
      </Typography>
      <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, maxWidth: 780 }}>
        Each restaurant is paid its share of a Stripe order by transfer, once the order is finished.
        A restaurant is only paid once Stripe reports <strong>transfers active</strong>, and while the
        split switch is on. RushmyFood pays Stripe’s own fees. Restaurants pay a card processing fee out of their
        share on Stripe orders: the default is set under Configuration → Stripe and can be changed per restaurant
        below. A change only applies to orders placed after it.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5, mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search restaurants"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 280 }}
        />
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          Refresh list
        </Button>
        {loading && <CircularProgress size={18} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {globalData && !globalOn && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The Stripe section is turned off for all restaurant dashboards. The switches below take effect once
          it is turned on under Configuration → Stripe.
        </Alert>
      )}

      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 2, overflow: 'hidden' }}>
        {rows.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>No restaurants.</Box>
        )}
        {rows.map((row, i) => {
          const connected = !!row.accountId
          const ready = row.transfersActive
          return (
            <Box
              key={row.restaurantId}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75,
                borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                <Typography sx={{ fontWeight: 700, color: INK }}>{row.restaurantName}</Typography>
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  {connected ? row.accountId : 'No Stripe account yet'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flex: '1 1 320px' }}>
                {connected ? (
                  <>
                    <Stage done={row.detailsSubmitted} label={row.detailsSubmitted ? 'Details submitted' : 'Details pending'} />
                    <Stage done={row.transfersActive} label={row.transfersActive ? 'Transfers active' : 'Transfers pending'} />
                    <Stage done={row.payoutsEnabled} label={row.payoutsEnabled ? 'Payouts to bank on' : 'Bank pending'} />
                  </>
                ) : (
                  <Chip size="small" label="Not connected" sx={{ height: 22, fontSize: 12, bgcolor: '#f0f0f3', color: MUTED }} />
                )}
              </Box>

              <Box sx={{ minWidth: 150, textAlign: 'right' }}>
                <Typography sx={{ fontSize: 12, color: MUTED }}>Waiting to be paid</Typography>
                <Typography sx={{ fontWeight: 700, color: row.pendingAmount > 0 ? WAIT : MUTED }}>
                  {usd(row.pendingAmount)}
                  {row.pendingOrders > 0 && (
                    <span style={{ color: MUTED, fontWeight: 500, fontSize: 12 }}> · {row.pendingOrders} orders</span>
                  )}
                </Typography>
              </Box>

              <Box sx={{ minWidth: 150, textAlign: 'right' }}>
                <Typography sx={{ fontSize: 12, color: MUTED }}>Processing fee</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: INK }}>
                    {row.processingFeePercent != null ? feeLabel(row.processingFeePercent, row.processingFeeFixed) : '—'}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ minWidth: 0, px: 0.75, textTransform: 'none' }}
                    onClick={() => setFeeEdit({
                      row,
                      percent: row.processingFeePercentOverride != null ? String(row.processingFeePercentOverride) : '',
                      fixed: row.processingFeeFixedOverride != null ? Number(row.processingFeeFixedOverride).toFixed(2) : '',
                    })}
                  >
                    Edit
                  </Button>
                </Box>
                <Typography sx={{ fontSize: 11, color: MUTED }}>
                  {row.processingFeePercentOverride == null && row.processingFeeFixedOverride == null ? 'default' : 'custom for this restaurant'}
                </Typography>
              </Box>

              <Tooltip title="Show Stripe (balance, payouts, onboarding, transactions, refunds) in this restaurant's dashboard">
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 130 }}>
                  <Switch
                    size="small"
                    checked={!!row.dashboardEnabled}
                    disabled={busy === `dashboard-${row.restaurantId}`}
                    onChange={(e) => {
                      const enabled = e.target.checked
                      run(`dashboard-${row.restaurantId}`, async () => {
                        await setDashboard({ variables: { restaurantId: row.restaurantId, enabled } })
                        await refetch()
                      })
                    }}
                    inputProps={{ 'aria-label': `Stripe in ${row.restaurantName} dashboard` }}
                  />
                  <Typography sx={{ fontSize: 12, color: row.dashboardEnabled ? INK : MUTED }}>
                    Dashboard {row.dashboardEnabled ? 'on' : 'off'}
                  </Typography>
                </Box>
              </Tooltip>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!connected && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy === `create-${row.restaurantId}`}
                    onClick={() => run(`create-${row.restaurantId}`, async () => {
                      await createAccount({ variables: { restaurantId: row.restaurantId } })
                      await refetch()
                      setToast({ severity: 'success', text: 'Stripe account created. Send the owner the onboarding link next.' })
                    })}
                  >
                    Create account
                  </Button>
                )}
                {connected && !ready && (
                  <Tooltip title="Anyone with this link can set the bank account this restaurant is paid to. Send it to the owner directly.">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ContentCopyIcon />}
                      disabled={busy === `link-${row.restaurantId}`}
                      onClick={() => copyLink(row)}
                    >
                      Onboarding link
                    </Button>
                  </Tooltip>
                )}
                {connected && (
                  <Button
                    size="small"
                    disabled={busy === `refresh-${row.restaurantId}`}
                    onClick={() => run(`refresh-${row.restaurantId}`, async () => {
                      await refreshAccount({ variables: { restaurantId: row.restaurantId } })
                      await refetch()
                    })}
                  >
                    Refresh
                  </Button>
                )}
                {ready && row.pendingAmount > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: GOOD, color: GOOD }}
                    disabled={busy === `payout-${row.restaurantId}`}
                    onClick={() => run(`payout-${row.restaurantId}`, async () => {
                      const res = await payOutPending({ variables: { restaurantId: row.restaurantId } })
                      const r = res?.data?.payOutPendingStripeTransfers
                      await refetch()
                      setToast({
                        severity: r?.transferred > 0 ? 'success' : 'info',
                        text: r?.transferred > 0
                          ? `Paid ${r.transferred} order(s), ${usd(r.paid)}.`
                          : 'Nothing could be paid out — check the split switch and that transfers are active.',
                      })
                    })}
                  >
                    Pay out pending
                  </Button>
                )}
                {connected && (
                  <Button
                    size="small"
                    color="error"
                    disabled={busy === `remove-${row.restaurantId}`}
                    onClick={() => setRemoving(row)}
                  >
                    Remove account
                  </Button>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>

      <Dialog open={!!feeEdit} onClose={() => setFeeEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Processing fee for {feeEdit?.row?.restaurantName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
            Taken from this restaurant’s share on each Stripe order: a percent of the customer’s card charge plus a fixed
            amount. Leave a field blank to use the default. Only orders placed after saving are affected.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small" type="number" label="Percent" value={feeEdit?.percent ?? ''}
              onChange={(e) => setFeeEdit({ ...feeEdit, percent: e.target.value })}
              inputProps={{ min: 0, max: 20, step: 0.1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              placeholder="default"
            />
            <Typography sx={{ color: MUTED, fontWeight: 700 }}>+</Typography>
            <TextField
              size="small" type="number" label="Fixed" value={feeEdit?.fixed ?? ''}
              onChange={(e) => setFeeEdit({ ...feeEdit, fixed: e.target.value })}
              inputProps={{ min: 0, max: 5, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              placeholder="default"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeEdit({ ...feeEdit, percent: '', fixed: '' })}>Use default</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setFeeEdit(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const edit = feeEdit
              setFeeEdit(null)
              const toNum = (v) => (v === '' || v == null ? null : parseFloat(v))
              run(`fee-${edit.row.restaurantId}`, async () => {
                await setFee({ variables: { restaurantId: edit.row.restaurantId, percent: toNum(edit.percent), fixed: toNum(edit.fixed) } })
                await refetch()
                setToast({ severity: 'success', text: `Processing fee for ${edit.row.restaurantName} saved. New Stripe orders use it.` })
              })
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!removing} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Remove {removing?.restaurantName}’s Stripe account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: MUTED }}>
            Deletes <strong>{removing?.accountId}</strong> at Stripe. This cannot be undone. The restaurant can set up
            payouts again from the beginning. Stripe refuses while any money is unsettled: orders not yet sent, a balance,
            or a payout on its way.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const row = removing
              setRemoving(null)
              run(`remove-${row.restaurantId}`, async () => {
                await removeAccount({ variables: { restaurantId: row.restaurantId } })
                await refetch()
                setToast({ severity: 'success', text: `${row.restaurantName}’s Stripe account was removed.` })
              })
            }}
          >
            Remove account
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.text}</Alert> : null}
      </Snackbar>
    </Container>
  )
}
